import logo from "assets/images/logo.png";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineLogout } from "react-icons/ai";
import { FaRegCircleUser } from "react-icons/fa6";
import { RxCross1 } from "react-icons/rx";
import { HiMenu } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { IoChevronDown } from "react-icons/io5";

import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useLogoutMutation } from "apis/user";
import Avatar from "assets/images/profile (1).png";
import Button from "./Button";

// ============================================
// Role constants
// ============================================
const SUPER = "superAdmin";
const BELEEF = "beleefAdmin";
const MELO = "meloAdmin";
const ALL = [SUPER, BELEEF, MELO];

// ============================================
// Nav structure — grouped by what each item is.
// Each group has a heading + items; each item lists
// which roles can see it. Icons intentionally removed.
// ============================================
const NAV_GROUPS = [
  {
    heading: "Overview",
    items: [
      { name: "Dashboard", route: "/", roles: ALL },
      { name: "Pilot", route: "/pilot-chat", roles: ALL },
      { name: "Billing Report", route: "/financial-dashboard", roles: [SUPER] },
      { name: "Financial Summary", route: "/financial-summary", roles: [SUPER] },
      { name: "Reports", route: "/reports", roles: [SUPER] },
      { name: "Deposits", route: "/deposit", roles: [SUPER] },
      { name: "Users Activity", route: "/user-activity", roles: [SUPER] },
      { name: "Page Visits", route: "/page-visits", roles: [SUPER] },
      { name: "AI Usage & Cost", route: "/ai-usage-logs", roles: [SUPER] },
      { name: "Pilot Conversations", route: "/pilot-conversations", roles: [SUPER] },
      { name: "Pilot Knowledge", route: "/pilot-knowledge", roles: [SUPER] },
      { name: "Registry Search Usage", route: "/registry-search-usage", roles: [SUPER] },
    ],
  },
  {
    heading: "Users & Access",
    items: [
      { name: "Users", route: "/users", roles: [SUPER] },
      { name: "Add Admin", route: "/admin", roles: [SUPER] },
      { name: "Subscriptions", route: "/subscriptions", roles: [SUPER] },
      { name: "Settings", route: "/settings", roles: ALL },
      { name: "Change Password", route: "/change-password", roles: ALL },
    ],
  },
  {
    heading: "Organizations & Offices",
    items: [
      { name: "Network", route: "/network", roles: [SUPER] },
      { name: "Division", route: "/division", roles: [SUPER] },
      { name: "Authority Grants", route: "/role-grant", roles: [SUPER] },
      { name: "Template Library", route: "/template-library", roles: [SUPER] },
      { name: "Company", route: "/company", roles: [SUPER] },
      { name: "Organization", route: "/organization", roles: [SUPER] },
      {
        name: "Contractor Company",
        route: "/contractor-company",
        roles: [SUPER],
      },
      { name: "Office", route: "/office", roles: [SUPER] },
      { name: "Contractors", route: "/contractors", roles: [SUPER] },
      { name: "Agency Party", route: "/agency-party", roles: [SUPER] },
      { name: "Suburb", route: "/suburb", roles: [SUPER] },
    ],
  },
  {
    heading: "Properties",
    items: [
      { name: "Property", route: "/property", roles: [SUPER, BELEEF] },
      { name: "Calendar", route: "/events", roles: [SUPER, MELO] },
      { name: "Event Types", route: "/event-types", roles: [SUPER] },
      { name: "Campaign Events", route: "/campaign-events", roles: [SUPER] },
    ],
  },
  {
    heading: "Marketing & Campaigns",
    items: [
      {
        name: "Buyer Campaign Management",
        route: "/buyerCampaignManagement",
        roles: ALL,
      },
      { name: "Social Post", route: "/socialPost", roles: ALL },
      { name: "Campaign Faqs", route: "/campaignFaq", roles: ALL },
      { name: "Campaign Flow", route: "/campaign-flow", roles: [SUPER] },
      { name: "Open Home Music", route: "/openHomeMusic", roles: [SUPER] },
    ],
  },
  {
    heading: "Media & Edits",
    items: [
      { name: "Edits", route: "/edits", roles: [SUPER, MELO] },
      {
        name: "Gemini Validation Logs",
        route: "/gemini-validation-logs",
        roles: [SUPER],
      },
      { name: "Upload Video", route: "/videos", roles: [SUPER] },
      { name: "Train Me Library", route: "/train-me-library", roles: [SUPER] },
    ],
  },
  {
    heading: "Products & Orders",
    items: [
      { name: "Products", route: "/products", roles: [SUPER] },
      { name: "Packages", route: "/packages", roles: [SUPER] },
      { name: "Package Rules", route: "/package-rules", roles: [SUPER] },
      { name: "Services List", route: "/services-list", roles: ALL },
      { name: "Shop", route: "/shop", roles: ALL },
      { name: "Prints", route: "/prints", roles: ALL },
      { name: "Orders", route: "/orders", roles: [SUPER] },
      { name: "Company Orders", route: "/company-orders", roles: [SUPER] },
    ],
  },
  {
    heading: "Comms & Templates",
    items: [
      { name: "SMS Templates", route: "/sms-templates", roles: [SUPER] },
      { name: "Sms Logs", route: "/sms-logs", roles: [SUPER] },
      {
        // Same reasoning as Vendor Comms Log below — dev ops read bounces and
        // spam reports here, so beleefAdmin needs it too.
        name: "Email Logs",
        route: "/email-logs",
        roles: [SUPER, BELEEF],
      },
      {
        // Dev ops read delivery failures here, so beleefAdmin needs it too.
        name: "Vendor Comms Log",
        route: "/comms-property-vendor",
        roles: [SUPER, BELEEF],
      },
      {
        name: "CommsLog Vendor Templates",
        route: "/commslog-vendor-templates",
        roles: [SUPER],
      },
      {
        name: "CommsLog Buyer Templates",
        route: "/commslog-buyer-templates",
        roles: [SUPER],
      },
      {
        name: "CommsLog Vendor Tags",
        route: "/commslog-vendor-tags",
        roles: [SUPER],
      },
    ],
  },
  {
    heading: "Community & Alerts",
    items: [
      {
        name: "Product announcements",
        route: "/product-announcements",
        roles: [SUPER],
      },
      {
        name: "Community Trigger Options",
        route: "/community-trigger-options",
        roles: [SUPER],
      },
      {
        name: "Community Triggers",
        route: "/community-triggers",
        roles: [SUPER],
      },
      { name: "Contact Alerts", route: "/contact-alerts", roles: [SUPER] },
      { name: "Add Alert", route: "/add-alert", roles: ALL },
    ],
  },
  {
    heading: "AI & Automation",
    items: [
      { name: "AI Call", route: "/ai-call", roles: [SUPER] },
      { name: "AI Compliance", route: "/ai-compliance", roles: [SUPER] },
      { name: "Prompt", route: "/prompts", roles: [SUPER] },
      { name: "Rental Prompts", route: "/rental-prompts", roles: [SUPER] },
      { name: "Logical Price", route: "/logical-price", roles: [SUPER] },
    ],
  },
  {
    heading: "Compliance & Legal",
    items: [
      { name: "Legal Documents", route: "/legal-documents", roles: [SUPER] },
      { name: "Legislation Register", route: "/legislation-register", roles: [SUPER] },
      { name: "Jurisdiction Rules", route: "/jurisdiction-rules", roles: [SUPER] },
      { name: "AML", route: "/aml", roles: [SUPER] },
      { name: "Waitlist", route: "/Waitlist", roles: [SUPER] },
    ],
  },
  {
    heading: "Admin Overrides",
    items: [
      {
        name: "Admin Overrides",
        route: "/admin-overrides",
        roles: [SUPER],
      },
      {
        name: "Change Address",
        route: "/address-change",
        roles: [SUPER],
      },
    ],
  },
];

// ============================================
// Navbar Component
// ============================================
const Navbar = () => {
  const location = useLocation();
  const { mutate: logout } = useLogoutMutation();
  const { isAuthenticated, role, user } = useContext(AuthContext);

  // Read from the context rather than a second /me call. The old useMeQuery
  // path returned only the decoded JWT — no name, no email — so these two
  // lines rendered blank in the dropdown.
  const { name, email } = user || {};

  const [isProfileDropdown, setIsProfileDropdown] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const profileDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const toggleGroup = (heading) =>
    setCollapsed((prev) => ({ ...prev, [heading]: !prev[heading] }));

  // Filter items by role, then by search query. Groups with no visible
  // items are dropped entirely. Search matches item names AND group
  // headings, so typing a category name surfaces the whole group.
  const visibleGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return NAV_GROUPS.map((group) => {
      const roleItems = group.items.filter((item) => item.roles.includes(role));

      if (!q) return { ...group, items: roleItems };

      const headingMatches = group.heading.toLowerCase().includes(q);
      const items = headingMatches
        ? roleItems
        : roleItems.filter((item) => item.name.toLowerCase().includes(q));

      return { ...group, items };
    }).filter((group) => group.items.length > 0);
  }, [role, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setSearchQuery("");
  };

  const isActiveRoute = (route) =>
    route === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(route);

  return (
    <nav className="bg-white sticky top-0" style={{ zIndex: 1001 }}>
      <div className="container flex items-center justify-between py-4">
        <Button onClick={() => setIsSidebarOpen((prev) => !prev)}>
          <HiMenu size={24} className="text-gray-700" />
        </Button>

        <div className="navbar-image flex items-center gap-4">
          <Link to="/">
            <img src={logo} alt="Beleef" className="w-[150px] h-auto" />
          </Link>
        </div>

        {!user || !isAuthenticated ? (
          <Link to="/login" className="text-sm">
            Sign In
          </Link>
        ) : (
          <div className="relative">
            <img
              src={Avatar}
              className="w-8 h-8 rounded-full cursor-pointer profile"
              alt="Profile"
              onClick={() => setIsProfileDropdown((prev) => !prev)}
            />
            {isProfileDropdown && (
              <div className="profile-dropdown-menu" ref={profileDropdownRef}>
                <div className="flex items-center mb-4 px-2">
                  <img
                    src={Avatar}
                    className="w-10 h-10 rounded-full object-fill"
                    alt="Profile"
                  />
                  <div className="ml-3">
                    <p className="truncate w-44 text-sm font-semibold mb-0">
                      {name}
                    </p>
                    <p className="truncate w-44 text-sm text-darkergray mb-0">
                      {email}
                    </p>
                  </div>
                </div>
                <hr className="my-1" />
                {/* Was /profile, which has never had a route in App.js and
                    landed on NotFound. */}
                <Link className="profile-dropdown-item gap-2" to="/settings">
                  <FaRegCircleUser />
                  <span>Settings</span>
                </Link>
                <Link className="profile-dropdown-item gap-2" onClick={logout}>
                  <AiOutlineLogout />
                  Logout
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 bg-white shadow-lg w-full max-w-[320px] transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out flex flex-col`}
        style={{ zIndex: 9999 }}
      >
        {/* Header row: close button */}
        <div
          className="mt-12 flex justify-end pr-4 cursor-pointer flex-shrink-0"
          onClick={closeSidebar}
        >
          <RxCross1 />
        </div>

        {/* Search */}
        <div className="px-4 pt-2 pb-3 flex-shrink-0">
          <div className="flex items-center gap-2 border border-mediumgray bg-white rounded-md px-3 py-2 w-full focus-within:border-darkgray transition-colors">
            <IoSearchOutline
              size={16}
              className="text-darkgray flex-shrink-0"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search..."
              className="flex-1 bg-transparent outline-none text-sm text-black placeholder-darkgray"
            />
          </div>
        </div>

        {/* Grouped nav — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {visibleGroups.length === 0 ? (
            <p className="text-sm text-darkgray px-2 py-4">
              No matches for “{searchQuery.trim()}”.
            </p>
          ) : (
            visibleGroups.map((group) => {
              // While searching, force groups open so results are visible.
              const isCollapsed = isSearching
                ? false
                : !!collapsed[group.heading];

              return (
                <div key={group.heading} className="mb-4">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.heading)}
                    disabled={isSearching}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-darkgray"
                  >
                    <span>{group.heading}</span>
                    {!isSearching && (
                      <IoChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${
                          isCollapsed ? "-rotate-90" : "rotate-0"
                        }`}
                      />
                    )}
                  </button>

                  {!isCollapsed && (
                    <div className="mt-1 flex flex-col">
                      {group.items.map((item) => {
                        const active = isActiveRoute(item.route);
                        return (
                          <Link
                            key={item.route}
                            to={item.route}
                            onClick={closeSidebar}
                            className={`pl-4 pr-2 py-2 text-sm rounded-md transition-colors ${
                              active
                                ? "bg-lightgray text-black font-medium"
                                : "text-darkergray hover:bg-lightgray"
                            }`}
                          >
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}
    </nav>
  );
};

export default Navbar;
